import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { firebasePlatformService, Platform } from '../services/FirebasePlatformService';
import './Platforms.css';

const Platforms: React.FC = () => {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlatforms();
  }, []);

  const loadPlatforms = async () => {
    try {
      console.log('Loading platforms from Firebase...');
      
      // Try to get all platforms first, then filter active ones
      const allPlatforms = await firebasePlatformService.getAllPlatforms();
      console.log('All platforms from Firebase:', allPlatforms);
      
      // Filter only active platforms
      const activePlatforms = allPlatforms.filter(platform => platform.isActive === true);
      console.log('Active platforms:', activePlatforms);
      
      setPlatforms(activePlatforms);
      
      // If no platforms found, log more info
      if (activePlatforms.length === 0) {
        console.log('No active platforms found. All platforms count:', allPlatforms.length);
        console.log('First platform (if any):', allPlatforms[0]);
      }
      
    } catch (error) {
      console.error('Failed to load platforms:', error);
      console.error('Error details:', error);
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  if (loading) {
    return (
      <div className="platforms-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading platforms...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="platforms-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      transition={{ staggerChildren: 0.2 }}
    >
      <div className="container">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Trading Platforms</h1>
          <p>Access professional trading tools on our recommended platforms</p>
        </motion.div>
        
        <motion.div className="platform-grid" variants={containerVariants}>
          {platforms.map((platform, index) => (
            <motion.div 
              key={platform.id}
              className="platform-card" 
              variants={itemVariants} 
              whileHover={{ y: -10 }}
              transition={{ delay: index * 0.1 }}
            >
              {platform.logo && (
                <div className="platform-logo">
                  <img src={platform.logo} alt={platform.name} />
                </div>
              )}
              <h3>{platform.name}</h3>
              <p>{platform.description}</p>
              
              {platform.features.length > 0 && (
                <div className="platform-highlights">
                  <ul>
                    {platform.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="platform-specs">
                {platform.price && (
                  <span className="spec">Price: {platform.price}</span>
                )}
                {platform.version && (
                  <span className="spec">Version: {platform.version}</span>
                )}
                {platform.fileSize && (
                  <span className="spec">Size: {platform.fileSize}</span>
                )}
              </div>

              <div className="platform-actions">
                {platform.downloadLink && (
                  <a 
                    href={platform.downloadLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Download
                  </a>
                )}
                <Link 
                  to={`/platforms/${platform.name.toLowerCase().replace(/\s+/g, '')}`} 
                  className="btn btn-secondary"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {platforms.length === 0 && (
          <motion.div className="empty-platforms" variants={itemVariants}>
            <h3>No platforms available</h3>
            <p>Check back soon for our trading platforms</p>
          </motion.div>
        )}
        
        <motion.div className="platform-info" variants={itemVariants}>
          <h2>Professional Trading Platforms</h2>
          <p>We recommend only the most reliable and feature-rich trading platforms for our clients. All our recommended platforms offer professional charting tools, technical indicators, and real-time market data to help you execute your trading strategies effectively.</p>
          
          <div className="platform-features">
            <motion.div className="feature" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
              <h3>Advanced Charting</h3>
              <p>Professional charting tools with multiple timeframes and indicators</p>
            </motion.div>
            <motion.div className="feature" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
              <h3>Automated Trading</h3>
              <p>Support for algorithmic trading and expert advisors</p>
            </motion.div>
            <motion.div className="feature" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
              <h3>Real-Time Data</h3>
              <p>Access to real-time market prices and economic calendars</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Platforms;